import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import AuditTrailTimeline from './AuditTrailTimeline';
import { useAcknowledgementAuditTrailDetail } from '../../hooks/useAcknowledgementAuditTrailDetail';
import { AUDIT_TRAIL_HISTORY_TABS } from '../../constants/acknowledgementAuditTrail.constants';
import type {
  AuditTrailDetailContext,
  AuditTrailHistoryTab,
} from '../../types/acknowledgementAuditTrail.types';
import {
  formatAcknowledgementChannel,
  formatAuditTimestamp,
} from '../../utils/acknowledgementAuditTrailUtils';
import { formatCriticalityLabel } from '../../utils/criticalResultsDashboardUtils';
import { ICON_STROKE_WIDTH } from '../../constants';
import '../../pages/admin/EscalationMonitor.css';

interface AcknowledgementAuditTrailDetailModalProps {
  open: boolean;
  context: AuditTrailDetailContext | null;
  onClose: () => void;
  initialTab?: AuditTrailHistoryTab;
}

function DetailField({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="crd-notification-detail-field">
      <span className="crd-notification-detail-label">{label}</span>
      <div className="crd-notification-detail-value">{children}</div>
    </div>
  );
}

function AuditDetailSummary({
  detail,
  eventCount,
}: Readonly<{
  detail: NonNullable<ReturnType<typeof useAcknowledgementAuditTrailDetail>['detail']>;
  eventCount: number;
}>) {
  const acknowledgedBy =
    detail.acknowledgementSummary.acknowledgedByUser?.fullName ||
    detail.acknowledgementSummary.acknowledgedByUser?.username ||
    '—';
  const methodSuffix = detail.acknowledgementSummary.method
    ? ` · ${formatAcknowledgementChannel(detail.acknowledgementSummary.method)}`
    : '';
  const patientExtra = detail.patient?.risVisitId ? ` · ${detail.patient.risVisitId}` : '';

  return (
    <section
      className="crd-notification-detail-summary"
      aria-label="Audit detail"
    >
      <DetailField label="Patient">
        {detail.patient?.name || '—'}
        {patientExtra}
      </DetailField>
      <DetailField label="Study">
        {detail.study?.studyDescription || detail.study?.studyInstanceUid || '—'}
      </DetailField>
      <DetailField label="Criticality">
        {formatCriticalityLabel(String(detail.notificationSummary.criticalityBand))}
      </DetailField>
      <DetailField label="Notification status">{detail.notificationSummary.status}</DetailField>
      <DetailField label="Acknowledgement status">
        {detail.acknowledgementSummary.status}
      </DetailField>
      <DetailField label="Acknowledged by">
        {acknowledgedBy}
        {methodSuffix}
      </DetailField>
      <DetailField label="Acknowledged at">
        {formatAuditTimestamp(detail.acknowledgementSummary.acknowledgedAt)}
      </DetailField>
      <DetailField label="Escalation level">
        {detail.escalationSummary?.currentLevel ? `L${detail.escalationSummary.currentLevel}` : '—'}
      </DetailField>
      <DetailField label="Audit events">{eventCount}</DetailField>
    </section>
  );
}

function renderDetailBody(
  detailLoading: boolean,
  detailError: string | null | undefined,
  detail: ReturnType<typeof useAcknowledgementAuditTrailDetail>['detail'],
  eventCount: number
) {
  if (detailLoading) {
    return <p className="esc-monitor-placeholder">Loading audit detail…</p>;
  }
  if (detailError) {
    return (
      <p
        className="crd-error"
        role="alert"
      >
        {detailError}
      </p>
    );
  }
  if (detail) {
    return (
      <AuditDetailSummary
        detail={detail}
        eventCount={eventCount}
      />
    );
  }
  return null;
}

export default function AcknowledgementAuditTrailDetailModal({
  open,
  context,
  onClose,
  initialTab = 'auditTimeline',
}: Readonly<AcknowledgementAuditTrailDetailModalProps>) {
  const {
    detail,
    activeTab,
    setActiveTab,
    activeEvents,
    detailLoading,
    timelineLoading,
    detailError,
    timelineError,
    eventCount,
  } = useAcknowledgementAuditTrailDetail({ open, context, initialTab });

  const title = context?.accessionLabel || 'Audit record';
  const subtitle = context?.studyLabel || '';

  const emptyMessageByTab: Record<AuditTrailHistoryTab, string> = {
    auditTimeline: 'No audit timeline events recorded yet.',
    notificationHistory: 'No notification history recorded yet.',
    acknowledgementHistory: 'No acknowledgement history recorded yet.',
    reminderHistory: 'No reminder history recorded yet.',
    escalationHistory: 'No escalation history recorded yet.',
    deliveryStatus: 'No delivery status events recorded yet.',
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => !isOpen && onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="esc-monitor-modal-overlay" />
        <Dialog.Content className="esc-monitor-modal crd-notification-detail-modal aat-detail-modal">
          <header className="esc-monitor-modal-header">
            <div>
              <Dialog.Title className="esc-monitor-modal-title">{title}</Dialog.Title>
              {subtitle ? (
                <Dialog.Description className="esc-monitor-modal-subtitle">
                  {subtitle}
                </Dialog.Description>
              ) : null}
            </div>
            <div className="esc-monitor-modal-actions">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="esc-monitor-modal-close"
                  aria-label="Close"
                >
                  <X
                    size={18}
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                </button>
              </Dialog.Close>
            </div>
          </header>

          <div className="esc-monitor-modal-body crd-notification-detail-body">
            {renderDetailBody(detailLoading, detailError, detail, eventCount)}

            <div
              className="crd-notification-history-tabs"
              role="tablist"
              aria-label="Audit history"
            >
              {AUDIT_TRAIL_HISTORY_TABS.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`crd-notification-history-tab${
                    activeTab === tab.id ? 'crd-notification-history-tab--active' : ''
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {timelineError ? (
              <p
                className="crd-error"
                role="alert"
              >
                {timelineError}
              </p>
            ) : null}

            {timelineLoading ? (
              <p className="esc-monitor-placeholder">Loading audit timeline…</p>
            ) : (
              <AuditTrailTimeline
                events={activeEvents}
                emptyMessage={emptyMessageByTab[activeTab]}
              />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
